import React from 'react'
import { CSSObject } from '@emotion/react'

import {
  AlignItems,
  Body1,
  Box,
  Button,
  Color,
  CSSKeywords,
  Display,
  Display3,
  FlexDirection,
  Icon,
  IconVariant,
  ResponsiveValue,
  Space,
  Visible,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import chickPeasSmall from '../../../assets/ValueYouCanTasteSectionAssets/chickpeas-small.svg'
import chickPeasLarge from '../../../assets/ValueYouCanTasteSectionAssets/chickpeas-large.svg'
import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../../tracking'
import { InlineLink } from '../../InlineLink/InlineLink'

type BenefitsProps = {
  ctaText: string
  onCTAClick: () => void
}

const responsiveBackground: ResponsiveValue<string> = [
  `85% -12px / 639px no-repeat url(${chickPeasSmall})`,
  `-200px / auto 480px no-repeat url(${chickPeasLarge})`,
  null,
  `-150px / auto 480px no-repeat url(${chickPeasLarge})`,
  `-80px / auto 480px no-repeat url(${chickPeasLarge})`,
]

const Container = withCitrusTheme(Box, (theme) => ({
  ...(theme.responsiveStyle(responsiveBackground, ['background']) as CSSObject),
}))

export function Benefits({ ctaText, onCTAClick }: BenefitsProps) {
  const clickCTA = () => {
    onCTAClick()
    trackCTAClick(TrackingActions.CLICK_GET_STARTED, LandingPageContentSections.VALUE_YOU_CAN_TASTE)
  }

  return (
    <Container
      alignItems={[AlignItems.FlexStart, null, null, AlignItems.Center]}
      display={Display.Flex}
      height={[null, '100%']}
      paddingH={[6, 4, 6, 20]}
      paddingV={[14, 16]}
      paddingBottom={[0]}
    >
      <Box width={[null, null, null, '430px']}>
        <Box width={['75%', CSSKeywords.Unset]}>
          <Display3 color={Color.White}>Easy Dinners Never Tasted So Good</Display3>
        </Box>
        <Space size={[4, 5]} />
        <Box display={Display.Flex} flexDirection={FlexDirection.Row}>
          <Box color={Color.Success_400}>
            <Icon name="tick" variant={IconVariant.Inherit} />
          </Box>
          <Space direction="horizontal" size={1} />
          <Body1 color={Color.White}>
            Discover more{' '}
            <InlineLink
              href="https://www.gousto.co.uk/menu?collection=quick-easy"
              backgroundStyle="dark"
            >
              quick and easy recipes
            </InlineLink>{' '}
            than any other UK recipe box.
          </Body1>
        </Box>
        <Space size={2} />
        <Box display={Display.Flex} flexDirection={FlexDirection.Row}>
          <Box color={Color.Success_400}>
            <Icon name="tick" variant={IconVariant.Inherit} />
          </Box>
          <Space direction="horizontal" size={1} />
          <Body1 color={Color.White}>
            Get more bang for your box with meals from just{' '}
            <InlineLink href="https://www.gousto.co.uk/box-prices" backgroundStyle="dark">
              £3.09†
            </InlineLink>
            .
          </Body1>
        </Box>
        <Space size={2} />
        <Box display={Display.Flex} flexDirection={FlexDirection.Row}>
          <Box color={Color.Success_400}>
            <Icon name="tick" variant={IconVariant.Inherit} />
          </Box>
          <Space direction="horizontal" size={1} />
          <Body1 color={Color.White}>
            From comfort food to global flavours and{' '}
            <InlineLink
              href="https://www.gousto.co.uk/menu?collection=health-hub"
              backgroundStyle="dark"
            >
              healthy choices
            </InlineLink>
            , there&apos;s something for everyone.
          </Body1>
        </Box>
        <Space size={[0, 5]} />
        <Visible display={[Display.None, null, Display.Block]}>
          <Box width="100%">
            <Button width="320px" onClick={clickCTA} height="48px">
              {ctaText}
            </Button>
          </Box>
        </Visible>
      </Box>
    </Container>
  )
}
